/**
 * Connection-hover store — the seam that makes a cross-space connection a UNIT.
 *
 * Every rendered piece of a connection (the LCA edge, each descent-chain segment
 * on every open level) carries the connection's id(s). Hovering ANY piece writes
 * that id set here; every piece across every space reads it and lights up if it
 * shares an id — so the whole chain highlights at once, across zoom levels.
 *
 * Same external-store shape as `hoverStore`: the provider value is stable, and
 * the snapshot is the hovered id Set (a fresh ref only when it changes), so a
 * layer re-renders only when the set actually flips.
 */
import { createContext, useContext, useSyncExternalStore } from "react";

export interface ConnectionHoverStore {
  subscribe(onChange: () => void): () => void;
  getHovered(): ReadonlySet<string>;
  /** Replace the hovered connection-id set (null/[] clears). */
  setHovered(ids: readonly string[] | null): void;
}

const EMPTY: ReadonlySet<string> = new Set();

export function createConnectionHoverStore(): ConnectionHoverStore {
  let hovered: ReadonlySet<string> = EMPTY;
  const listeners = new Set<() => void>();
  const sameAs = (ids: readonly string[]) =>
    ids.length === hovered.size && ids.every((id) => hovered.has(id));
  return {
    subscribe(onChange) {
      listeners.add(onChange);
      return () => listeners.delete(onChange);
    },
    getHovered() {
      return hovered;
    },
    setHovered(ids) {
      if (!ids || ids.length === 0) {
        if (hovered.size === 0) return;
        hovered = EMPTY;
      } else {
        if (sameAs(ids)) return;
        hovered = new Set(ids);
      }
      for (const l of listeners) l();
    },
  };
}

const Ctx = createContext<ConnectionHoverStore | null>(null);
export const ConnectionHoverProvider = Ctx.Provider;

const NOOP: ConnectionHoverStore = {
  subscribe: () => () => {},
  getHovered: () => EMPTY,
  setHovered: () => {},
};

export function useConnectionHoverStore(): ConnectionHoverStore {
  return useContext(Ctx) ?? NOOP;
}

/** Subscribe to the hovered connection-id set — re-renders the calling layer
 *  only when the set changes. Each piece then checks membership inline. Use for a
 *  layer that renders many pieces in one component (e.g. PortsLayer). */
export function useHoveredConnections(): ReadonlySet<string> {
  const store = useConnectionHoverStore();
  return useSyncExternalStore(store.subscribe, store.getHovered, () => EMPTY);
}

/** Subscribe to "is any of THESE ids hovered?" — a boolean snapshot, so a
 *  per-piece component (e.g. each memoised EdgePath) re-renders ONLY when its own
 *  highlight flips, not on every unrelated hover. Mirrors `hoverStore`'s
 *  per-item boolean to avoid an N-subscriber re-render storm. */
export function useConnectionHovered(ids: readonly string[]): boolean {
  const store = useConnectionHoverStore();
  return useSyncExternalStore(
    store.subscribe,
    () => {
      const h = store.getHovered();
      for (const id of ids) if (h.has(id)) return true;
      return false;
    },
    () => false,
  );
}
